import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './CategoryTiles.css';

/**
 * Game-level category grid. Each tile represents a TCG/sports-card "game":
 * Pokémon, MTG, Yu-Gi-Oh!, One Piece, Lorcana, Sports Cards. Counts roll up
 * from all descendants (eras → series → sets). Games with zero stocked
 * products show a "Coming soon" badge instead of a count.
 *
 * The featured Pokémon tile keeps the latest-set showcase art behaviour from
 * the old version so the homepage still feels visually rich.
 */
const GAME_TINTS = {
  pokemon:      '#3b4cca',
  mtg:          '#d4a017',
  yugioh:       '#7c3aed',
  'one-piece':  '#dc2626',
  lorcana:      '#0ea5e9',
  'sports-cards': '#16a34a',
};
const GAME_FALLBACK_NAME = {
  pokemon: 'Pokémon',
  mtg: 'Magic: The Gathering',
  yugioh: 'Yu-Gi-Oh!',
  'one-piece': 'One Piece',
  lorcana: 'Lorcana',
  'sports-cards': 'Sports Cards',
};

export default function CategoryTiles() {
  const [allCats, setAllCats] = useState([]);
  const [showcase, setShowcase] = useState({ set: null, cards: [], byRarity: {} });

  useEffect(() => {
    fetch('/api/categories')
      .then(r => r.ok ? r.json() : [])
      .then(rows => setAllCats(Array.isArray(rows) ? rows : []))
      .catch(() => {});

    fetch('/api/categories/latest-set-showcase?limit=8')
      .then(r => r.ok ? r.json() : { set: null, cards: [] })
      .then(data => setShowcase({
        set: data?.set || null,
        cards: Array.isArray(data?.cards) ? data.cards : [],
        byRarity: data?.byRarity || {},
      }))
      .catch(() => {});
  }, []);

  // Build games list + rolled-up product counts. A game's count is the sum of
  // product_count across every descendant (era → series → set → product types).
  const games = useMemo(() => {
    if (!allCats.length) return [];
    const childrenOf = new Map();
    for (const c of allCats) {
      if (c.parent_id == null) continue;
      if (!childrenOf.has(c.parent_id)) childrenOf.set(c.parent_id, []);
      childrenOf.get(c.parent_id).push(c);
    }
    const treeCount = (id) => {
      let total = 0;
      const stack = [id];
      const seen = new Set();
      while (stack.length) {
        const cur = stack.pop();
        if (seen.has(cur)) continue;
        seen.add(cur);
        for (const ch of (childrenOf.get(cur) || [])) {
          total += Number(ch.product_count || 0);
          stack.push(ch.id);
        }
      }
      return total;
    };
    const descendantCount = (id) => {
      let n = 0;
      const stack = [id];
      const seen = new Set();
      while (stack.length) {
        const cur = stack.pop();
        if (seen.has(cur)) continue;
        seen.add(cur);
        for (const ch of (childrenOf.get(cur) || [])) { n++; stack.push(ch.id); }
      }
      return n;
    };
    return allCats
      .filter(c => (c.kind || '') === 'game' && !c.is_hidden)
      .map(g => ({
        ...g,
        rolled_count: Number(g.product_count || 0) + treeCount(g.id),
        descendant_count: descendantCount(g.id),
      }))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [allCats]);

  // Fallback when no games are seeded yet — show static placeholders so the
  // homepage doesn't go blank during/right after a deploy.
  const tiles = games.length ? games : Object.entries(GAME_FALLBACK_NAME).map(([slug, name], i) => ({
    id: `placeholder-${slug}`,
    slug,
    name,
    rolled_count: slug === 'pokemon' ? 1 : 0, // assume Pokémon has stock
    logo_url: null,
    _placeholder: true,
  }));

  return (
    <section className="category-tiles">
      <h2 className="category-tiles__heading">Shop by game</h2>
      {showcase.set?.name && (
        <p className="category-tiles__subheading">
          Featuring card art from {showcase.set.name}
        </p>
      )}
      <div className="category-tiles__grid">
        {tiles.map(g => {
          const tint = GAME_TINTS[g.slug] || '#3b4cca';
          // "Active" = customer can browse → game has either products OR a
          // seeded category tree (eras/series/sets). A game with 14 eras but
          // 0 products attached yet is still browsable, not "coming soon".
          const active = g.rolled_count > 0 || g.descendant_count > 0;
          // Image priority per tile:
          //   1) Admin-uploaded game logo (categories.logo_url)
          //   2) Pokémon ONLY: showcase set art from latest-set-showcase
          //   3) No image — render a clean tinted tile with the game name
          // The previous version leaked Pokémon's `showcase.set.logo` onto every
          // tile because the fallback chain didn't gate it by game.
          const featuredCard = g.slug === 'pokemon' ? showcase.byRarity?.any : null;
          const imgUrl = g.logo_url || featuredCard?.image_url || null;
          const isLogo = !featuredCard && !!g.logo_url;

          const tileBody = (
            <>
              {imgUrl && (
                <img
                  className="category-tile__art"
                  src={imgUrl}
                  alt=""
                  loading="lazy"
                  aria-hidden="true"
                />
              )}
              <div className="category-tile__body">
                <div className="category-tile__name">{g.name}</div>
                {active ? (
                  <div className="category-tile__count">
                    {g.rolled_count > 0
                      ? `${g.rolled_count.toLocaleString()} item${g.rolled_count === 1 ? '' : 's'}`
                      : 'Browse sets →'}
                  </div>
                ) : (
                  <div className="category-tile__coming-soon">Coming soon</div>
                )}
              </div>
            </>
          );

          const tileClass = `category-tile${imgUrl ? ' category-tile--image' : ''}${isLogo ? ' category-tile--logo' : ''}${active ? '' : ' category-tile--coming-soon'}`;
          const tileStyle = {
            '--tile-tint': tint,
            '--tile-image': imgUrl ? `url("${imgUrl}")` : undefined,
          };

          return active ? (
            <Link
              key={g.id}
              to={`/shop/${g.slug}`}
              className={tileClass}
              style={tileStyle}
            >
              {tileBody}
            </Link>
          ) : (
            <div
              key={g.id}
              className={tileClass}
              style={tileStyle}
              role="presentation"
              aria-label={`${g.name} — coming soon`}
            >
              {tileBody}
            </div>
          );
        })}
      </div>
    </section>
  );
}
